import React, { useCallback, useEffect, useState } from "react";
import { Lightbulb, ChevronRight, MessageCircle, Check, RotateCcw } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// Prepared recommendation only. The read endpoint never starts AI generation.
// "Erledigt · Nächste anzeigen" ueberspringt den aktuellen Bereich und holt den naechstwichtigen Hebel.
export default function FirstRecommendation({ load, accent = "#A3E635", onGo, onAskAi }) {
  const { t, lang } = useI18n();
  const copy = {
    de: { title: "Vorbereitete Empfehlung", loading: "Vorhandene Empfehlung wird geladen …", none: "Keine weitere vorbereitete Empfehlung vorhanden.", reset: "Zurücksetzen" },
    fr: { title: "Recommandation préparée", loading: "Chargement de la recommandation existante …", none: "Aucune autre recommandation préparée.", reset: "Réinitialiser" },
    it: { title: "Raccomandazione preparata", loading: "Caricamento della raccomandazione esistente …", none: "Nessun'altra raccomandazione preparata.", reset: "Reimposta" },
    en: { title: "Prepared recommendation", loading: "Loading the existing recommendation …", none: "No further prepared recommendation available.", reset: "Reset" },
  }[lang] || { title: "Vorbereitete Empfehlung", loading: "Vorhandene Empfehlung wird geladen …", none: "Keine weitere vorbereitete Empfehlung vorhanden.", reset: "Zurücksetzen" };
  const [state, setState] = useState({ loading: true, data: null });
  const [skip, setSkip] = useState([]);

  const fetchRec = useCallback((skipList) => {
    setState({ loading: true, data: null });
    Promise.resolve(load?.(skipList))
      .then((d) => setState({ loading: false, data: d && d.headline ? d : null }))
      .catch(() => setState({ loading: false, data: null }));
  }, [load]);

  useEffect(() => { setSkip([]); fetchRec([]); }, [fetchRec]);

  const eyebrow = (
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: accent }}>
      <Lightbulb className="w-4 h-4" /> {copy.title}
    </div>
  );

  if (state.loading) {
    return (
      <div className="rounded-2xl border p-4 mb-4" style={{ borderColor: `${accent}33`, background: `${accent}0D` }} data-testid="first-rec-loading">
        {eyebrow}
        <div className="mt-3 space-y-2 animate-pulse">
          <div className="h-4 w-2/3 rounded bg-white/10" />
          <div className="h-3 w-full rounded bg-white/10" />
          <div className="h-3 w-4/5 rounded bg-white/10" />
        </div>
        <div className="mt-2 text-xs text-gray-500">{copy.loading}</div>
      </div>
    );
  }
  const resetSkip = () => { setSkip([]); fetchRec([]); };
  if (!state.data) {
    if (!skip.length) return null;
    return (
      <div className="rounded-2xl border p-4 mb-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: `${accent}22`, background: `${accent}08` }} data-testid="first-rec-empty">
        <div className="min-w-0">{eyebrow}<p className="mt-1 text-sm text-gray-400">{copy.none}</p></div>
        <button onClick={resetSkip} data-testid="first-rec-empty-reset"
          className="inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg border border-white/10 text-xs font-semibold text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> {copy.reset}
        </button>
      </div>
    );
  }

  const { headline, body, cta_label, cta_section } = state.data;
  const handleNext = () => {
    const newSkip = [...new Set([...skip, cta_section])];
    setSkip(newSkip);
    fetchRec(newSkip);
  };

  return (
    <div className="rounded-2xl border p-4 mb-4" style={{ borderColor: `${accent}33`, background: `${accent}0D` }} data-testid="first-rec">
      {eyebrow}
      <div className="mt-2 font-bold text-[15px] text-white" data-testid="first-rec-headline">{headline}</div>
      <p className="mt-1 text-sm text-gray-300 leading-relaxed" data-testid="first-rec-body">{body}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {cta_section && cta_label && (
          <button onClick={() => onGo?.(cta_section)} data-testid="first-rec-cta"
            className="inline-flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg text-black transition-transform active:scale-[0.98] hover:brightness-105" style={{ background: accent }}>
            {cta_label} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={() => onAskAi?.()} data-testid="first-rec-ask"
          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors hover:bg-white/5" style={{ color: accent, borderColor: `${accent}55` }}>
          <MessageCircle className="w-3.5 h-3.5" /> {t("Frage stellen")}
        </button>
      </div>
      {(cta_section || skip.length > 0) && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-3">
          {cta_section ? (
            <button onClick={handleNext} data-testid="first-rec-next"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white transition-colors">
              <Check className="w-3.5 h-3.5" /> {t("Erledigt · Nächste anzeigen")}
            </button>
          ) : <span />}
          {skip.length > 0 && (
            <button onClick={resetSkip} data-testid="first-rec-reset"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-300 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> {t("Zurücksetzen")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
